import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField
} from '@mui/material';
import {
  subscriptionService,
  paymentService,
  SubscriptionPreview
} from '../../services/subscription';
import { PaymentMethod } from '../../services/subscription/subscription.service';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';

interface CreateSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onSuccess: () => void;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  type: number;
  pricing: {
    currency: string;
    basePriceCents: number;
    perUserPriceCents: number;
    perGbPriceCents: number;
    stripePriceId: string;
    monthly?: { pricePerMonth: number; pricePerYear?: number; stripePriceId: string };
    yearly?: { pricePerYear: number; pricePerMonth: number; stripePriceId: string };
  };
  limits: {
    maxTeams: number;
    maxConcurrentUsers: number;
    maxStorageBytes: number;
    maxFileSizeMb: number;
    aiFeaturesEnabled: boolean;
    analyticsEnabled: boolean;
    prioritySupport: boolean;
    enabledIntegrations: string[];
  };
  isActive: boolean;
}

const CreateSubscriptionDialog: React.FC<CreateSubscriptionDialogProps> = ({
  open,
  onClose,
  organizationId,
  onSuccess
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [referenceName, setReferenceName] = useState<string>('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [preview, setPreview] = useState<SubscriptionPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Load available plans when dialog opens
  useEffect(() => {
    if (open) {
      console.log('CreateSubscriptionDialog: Loading plans...');
      setIsLoadingPlans(true);
      setError(null);
      subscriptionService.getSubscriptionPlans()
        .then(fetchedPlans => {
          console.log('CreateSubscriptionDialog: Fetched plans:', fetchedPlans);
          const activePlans = fetchedPlans.filter(p => p.isActive);
          console.log('CreateSubscriptionDialog: Active plans:', activePlans);
          setPlans(activePlans);
          // Set default to first paid plan or first plan
          const defaultPlan = activePlans.find(p =>
            p.pricing.monthly?.pricePerMonth > 0 ||
            p.pricing.basePriceCents > 0  // Backwards compatibility
          ) || activePlans[0];
          console.log('CreateSubscriptionDialog: Default plan:', defaultPlan);
          if (defaultPlan) {
            setSelectedPlanId(defaultPlan.id);
          }
        })
        .catch(err => {
          console.error('CreateSubscriptionDialog: Failed to fetch plans:', err);
          setError('Failed to load subscription plans. Please try again.');
        })
        .finally(() => {
          setIsLoadingPlans(false);
        });
    }
  }, [open]);

  // Load payment methods when dialog opens
  useEffect(() => {
    if (open && organizationId) {
      console.log('CreateSubscriptionDialog: Loading payment methods for org:', organizationId);
      setIsLoadingPaymentMethods(true);
      paymentService.listPaymentMethods(organizationId)
        .then(methods => {
          console.log('CreateSubscriptionDialog: Fetched payment methods:', methods);
          setPaymentMethods(methods);
          // Auto-select default payment method
          const defaultMethod = methods.find(m => m.isDefault);
          if (defaultMethod) {
            setSelectedPaymentMethodId(defaultMethod.id);
          } else if (methods.length > 0) {
            setSelectedPaymentMethodId(methods[0].id);
          }
        })
        .catch(err => {
          console.error('Failed to fetch payment methods:', err);
          setError('Failed to load payment methods. Please add a payment method first.');
        })
        .finally(() => {
          setIsLoadingPaymentMethods(false);
        });
    }
  }, [open, organizationId]);

  // Fetch preview when plan or billing interval changes
  useEffect(() => {
    if (!selectedPlanId || !organizationId) {
      setPreview(null);
      return;
    }

    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!selectedPlan) {
      setPreview(null);
      return;
    }

    // Only fetch preview for paid plans
    const isPaidPlan = billingInterval === 'yearly'
      ? selectedPlan.pricing.yearly.pricePerYear > 0
      : selectedPlan.pricing.monthly.pricePerMonth > 0;

    if (!isPaidPlan) {
      setPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setIsLoadingPreview(true);
      try {
        const previewData = await subscriptionService.previewSubscriptionChange(
          undefined, // no subscription ID for new subscriptions
          organizationId,
          selectedPlanId,
          billingInterval,
          undefined // no add-ons for base plan purchase
        );

        setPreview(previewData);
      } catch (err) {
        console.error('Failed to fetch preview:', err);
        // Don't show error to user, just clear preview
        setPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    // Debounce the preview fetch
    const timeoutId = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedPlanId, billingInterval, organizationId, plans]);

  const handlePurchaseClick = () => {
    setError(null);

    // Validation
    if (!selectedPlanId) {
      setError('Please select a subscription plan');
      return;
    }

    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!selectedPlan) {
      setError('Selected plan not found');
      return;
    }

    // Require payment method for paid plans
    const isPaidPlan = billingInterval === 'yearly'
      ? selectedPlan.pricing.yearly.pricePerYear > 0
      : selectedPlan.pricing.monthly.pricePerMonth > 0;

    if (isPaidPlan && !selectedPaymentMethodId) {
      setError('Please select a payment method');
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmPurchase = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      await subscriptionService.createSubscription({
        organizationId,
        planId: selectedPlanId,
        name: referenceName || '',
        autoRenew: true,
        paymentMethodId: selectedPaymentMethodId || undefined,
        billingInterval  // Add billing interval to subscription creation
      });

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Failed to create subscription:', err);
      setError(err.message || 'Failed to create subscription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedPlanId('');
      setReferenceName('');
      setSelectedPaymentMethodId('');
      setError(null);
      setShowConfirmDialog(false);
      onClose();
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  const formatPrice = (priceCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceCents / 100);
  };

  const formatStorage = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb} GB`;
  };

  const renderPlanCard = (plan: SubscriptionPlan) => {
    const isSelected = selectedPlanId === plan.id;

    // Get pricing based on selected billing interval
    const pricing = billingInterval === 'yearly' ? plan.pricing.yearly : plan.pricing.monthly;
    const isFree = billingInterval === 'yearly' ? plan.pricing.yearly.pricePerYear === 0 : plan.pricing.monthly.pricePerMonth === 0;

    // Calculate monthly savings for yearly plans
    const monthlySavings = billingInterval === 'yearly'
      ? plan.pricing.monthly.pricePerMonth - plan.pricing.yearly.pricePerMonth
      : 0;

    return (
      <Card
        key={plan.id}
        variant="outlined"
        sx={{
          cursor: 'pointer',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderWidth: isSelected ? 2 : 1,
          bgcolor: isSelected ? 'action.selected' : 'background.paper'
        }}
        onClick={() => setSelectedPlanId(plan.id)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" fontWeight="medium">
                  {plan.name}
                </Typography>
                {isFree && (
                  <Chip label="Free" size="small" color="success" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {plan.description}
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                {isFree ? (
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    Free
                  </Typography>
                ) : billingInterval === 'yearly' ? (
                  <>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      {formatPrice(plan.pricing.yearly.pricePerMonth)}/month
                      {monthlySavings > 0 && (
                        <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1 }}>
                          (save {formatPrice(monthlySavings)}/mo)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Billed {formatPrice(plan.pricing.yearly.pricePerYear)} annually
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {formatPrice(plan.pricing.monthly.pricePerMonth)}/month
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Includes:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  • {plan.limits.maxConcurrentUsers} concurrent users
                </Typography>
                <Typography variant="body2">
                  • {formatStorage(plan.limits.maxStorageBytes)} storage
                </Typography>
                <Typography variant="body2">
                  • {plan.limits.maxTeams} teams
                </Typography>
                {plan.limits.analyticsEnabled && (
                  <Typography variant="body2">
                    • Analytics included
                  </Typography>
                )}
                {plan.limits.prioritySupport && (
                  <Typography variant="body2">
                    • Priority support
                  </Typography>
                )}
              </Box>
            </Box>

            <Radio
              checked={isSelected}
              onChange={() => setSelectedPlanId(plan.id)}
              sx={{ mt: -1, mr: -1 }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const defaultPaymentMethod = paymentMethods.find(m => m.isDefault);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create Subscription</DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        {/* Two-column layout */}
        <Box sx={{ display: 'flex', minHeight: 500, maxHeight: '70vh' }}>
          {/* Left Column - Scrollable Plans List */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflowY: 'auto',
            borderRight: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Billing Interval Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Billing Frequency</FormLabel>
              <RadioGroup
                row
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value as 'monthly' | 'yearly')}
              >
                <FormControlLabel
                  value="monthly"
                  control={<Radio />}
                  label="Monthly"
                />
                <FormControlLabel
                  value="yearly"
                  control={<Radio />}
                  label="Yearly (Save 10%)"
                />
              </RadioGroup>
            </FormControl>

            {/* Reference Name Field */}
            <TextField
              label="Reference Name (Optional)"
              value={referenceName}
              onChange={(e) => setReferenceName(e.target.value)}
              fullWidth
              placeholder="e.g., Production Subscription, Marketing Team"
              helperText="A friendly name to help you identify this subscription"
              sx={{ mb: 3 }}
            />

            {/* Plans Section */}
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Select a Plan
            </Typography>
            {isLoadingPlans ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {plans
                  .sort((a, b) => {
                    const aPrice = billingInterval === 'yearly' ? a.pricing.yearly.pricePerYear : a.pricing.monthly.pricePerMonth;
                    const bPrice = billingInterval === 'yearly' ? b.pricing.yearly.pricePerYear : b.pricing.monthly.pricePerMonth;
                    return aPrice - bPrice;
                  })
                  .map(plan => renderPlanCard(plan))}
              </Box>
            )}
          </Box>

          {/* Right Column - Sticky Summary Panel */}
          <Box sx={{
            width: 320,
            p: 3,
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            <Typography variant="h6" fontWeight="medium">
              Order Summary
            </Typography>

            {/* Selected Plan Info */}
            {selectedPlan ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Selected Plan
                </Typography>
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  {selectedPlan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedPlan.description}
                </Typography>
                {(() => {
                  const pricing = billingInterval === 'yearly' ? selectedPlan.pricing.yearly : selectedPlan.pricing.monthly;
                  const isFree = billingInterval === 'yearly' ? pricing.pricePerYear === 0 : pricing.pricePerMonth === 0;
                  const totalPrice = billingInterval === 'yearly' ? pricing.pricePerYear : pricing.pricePerMonth;

                  return isFree ? (
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      Free
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        {formatPrice(totalPrice)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {billingInterval === 'yearly' ? 'per year' : 'per month'}
                      </Typography>
                      {billingInterval === 'yearly' && (
                        <Typography variant="body2" color="success.main">
                          {formatPrice(selectedPlan.pricing.yearly.pricePerMonth)}/month equivalent
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            ) : (
              <Alert severity="info">
                Please select a plan to continue
              </Alert>
            )}

            {/* Payment Method Selection - Only show for paid plans */}
            {selectedPlan && (billingInterval === 'yearly' ? selectedPlan.pricing.yearly.pricePerYear > 0 : selectedPlan.pricing.monthly.pricePerMonth > 0) && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Payment Method
                </Typography>
                {isLoadingPaymentMethods ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : paymentMethods.length === 0 ? (
                  <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                    No payment methods available. Please add one first.
                  </Alert>
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={selectedPaymentMethodId}
                      onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    >
                      {paymentMethods.map((method) => (
                        <FormControlLabel
                          key={method.id}
                          value={method.id}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {method.brand?.toUpperCase()} •••• {method.last4}
                              </Typography>
                              {method.isDefault && (
                                <Chip label="Default" size="small" color="primary" />
                              )}
                            </Box>
                          }
                          sx={{
                            border: '1px solid',
                            borderColor: selectedPaymentMethodId === method.id ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            mb: 1,
                            p: 1,
                            bgcolor: selectedPaymentMethodId === method.id ? 'action.selected' : 'background.paper'
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </Box>
            )}

            {/* Total Summary - Stripe Accurate Pricing */}
            {selectedPlan && (() => {
              const isPaid = billingInterval === 'yearly'
                ? selectedPlan.pricing.yearly.pricePerYear > 0
                : selectedPlan.pricing.monthly.pricePerMonth > 0;
              const totalPrice = billingInterval === 'yearly'
                ? selectedPlan.pricing.yearly.pricePerYear
                : selectedPlan.pricing.monthly.pricePerMonth;

              return isPaid && (
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  {isLoadingPreview ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : preview ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(preview.subtotal_cents)}
                        </Typography>
                      </Box>
                      {preview.tax_cents > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Tax</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatPrice(preview.tax_cents)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body1" fontWeight="bold">Total</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatPrice(preview.immediate_charge_cents)}{billingInterval === 'yearly' ? '/year' : '/mo'}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    // Fallback to calculated pricing if preview fails
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(totalPrice)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body1" fontWeight="bold">Total</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatPrice(totalPrice)}{billingInterval === 'yearly' ? '/year' : '/mo'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Estimated. Actual charges may include taxes.
                      </Typography>
                    </>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handlePurchaseClick}
          variant="contained"
          disabled={isSubmitting || !selectedPlanId || (() => {
            if (!selectedPlan) return true;
            const isPaid = billingInterval === 'yearly'
              ? selectedPlan.pricing.yearly.pricePerYear > 0
              : selectedPlan.pricing.monthly.pricePerMonth > 0;
            return isPaid && !selectedPaymentMethodId;
          })()}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Processing...' : (() => {
            if (!selectedPlan) return 'Create Subscription';
            const isFree = billingInterval === 'yearly'
              ? selectedPlan.pricing.yearly.pricePerYear === 0
              : selectedPlan.pricing.monthly.pricePerMonth === 0;
            return isFree ? 'Create Subscription' : 'Buy Subscription';
          })()}
        </Button>
      </DialogActions>

      {/* Purchase Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Confirm Purchase"
        content={
          selectedPlan ? (
            <Box>
              <Typography gutterBottom>
                You are about to purchase the following subscription:
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {selectedPlan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedPlan.description}
                </Typography>

                {/* Show Stripe accurate pricing if available */}
                {preview ? (
                  <Box sx={{ mt: 1.5 }}>
                    {preview.tax_cents > 0 && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatPrice(preview.subtotal_cents)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Tax</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatPrice(preview.tax_cents)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: preview.tax_cents > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Total</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {formatPrice(preview.immediate_charge_cents)}{billingInterval === 'yearly' ? '/year' : '/month'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                    {(() => {
                      const price = billingInterval === 'yearly'
                        ? selectedPlan.pricing.yearly.pricePerYear
                        : selectedPlan.pricing.monthly.pricePerMonth;
                      const period = billingInterval === 'yearly' ? '/year' : '/month';
                      return `${formatPrice(price)}${period}`;
                    })()}
                  </Typography>
                )}

                {selectedPaymentMethodId && paymentMethods.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Payment method: {paymentMethods.find(pm => pm.id === selectedPaymentMethodId)?.brand?.toUpperCase()} •••• {paymentMethods.find(pm => pm.id === selectedPaymentMethodId)?.last4}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your subscription will auto-renew {billingInterval === 'yearly' ? 'annually' : 'monthly'}. You can cancel at any time.
              </Typography>
            </Box>
          ) : ''
        }
        confirmText="Confirm Purchase"
        cancelText="Go Back"
        onConfirm={handleConfirmPurchase}
        onCancel={() => setShowConfirmDialog(false)}
        severity="info"
        isLoading={isSubmitting}
      />
    </Dialog>
  );
};

export default CreateSubscriptionDialog;
